import React from 'react';

import { alpha, Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { IconColors } from 'src/components/icons/icon-utils';
import { SmallDoubleCheckIcon } from 'src/components/icons/SmallDoubleCheckIcon';
import { SmallSigningIcon } from 'src/components/icons/SmallSigningIcon';
import { SmallTemplatePublishedIcon } from 'src/components/icons/SmallTemplatePublishedIcon';
import { SmallWorkflowDraftingIcon } from 'src/components/icons/SmallWorkflowDraftingIcon';
import { SmallWorkflowSingedIcon } from 'src/components/icons/SmallWorkflowSingedIcon';
import { useCellStyles } from 'src/components/reports';
import type { TemplateListRow } from 'src/components/template/template-list/TemplateList';
import type { WorkflowListRow } from 'src/components/workflow/workflow-list/WorkflowsList';
import { Apollo } from 'src/gql/generated';
import {
  getWorkflowStateColor,
  getWorkflowStateDisplayString,
  WorkflowStateColors,
} from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

type Row = TemplateListRow | WorkflowListRow;

export function WorkflowListStateCell({
  row,
}: GridCellParams<Row, Row['state']>): JSX.Element | null {
  const classes = useCellStyles();
  return (
    <div className={classes.cell}>
      <WorkflowListStateChip workflow={row} />
    </div>
  );
}

const chipHeightPx = 24;
export const useStateChipStyles = makeStyles<{ backgroundColor: string }>()(
  (theme, { backgroundColor }) => ({
    container: {
      alignItems: 'center',
      backgroundColor: alpha(backgroundColor, 0.15),
      borderRadius: 100,
      boxSizing: 'border-box',
      display: 'flex',
      gap: theme.spacing(0.5),
      maxHeight: chipHeightPx,
      minHeight: chipHeightPx,
      paddingRight: theme.spacing(1.25),
    },
    text: {
      fontSize: 12,
      fontWeight: 600,
    },
  })
);

type WorkflowStateChipWorkflow = Pick<Apollo.Workflow, 'state' | '__typename'> & {
  publishedAt?: Apollo.Workflow['publishedAt'];
  variant?: Apollo.Workflow['variant'];
};

export function WorkflowListStateChip({
  workflow,
}: {
  workflow: WorkflowStateChipWorkflow;
}): JSX.Element | null {
  const backgroundColor = getWorkflowStateChipBackgroundColor(workflow);
  const { classes } = useStateChipStyles({ backgroundColor });

  return (
    <div className={classes.container}>
      <WorkflowStateChipIcon backgroundColor={backgroundColor} workflow={workflow} />
      <Typography noWrap className={classes.text}>
        {getWorkflowStateChipText(workflow)}
      </Typography>
    </div>
  );
}

export const useWorkflowStateIconStyles = makeStyles<{ backgroundColor: string }>()(
  (theme, { backgroundColor }) => ({
    iconContainer: {
      alignItems: 'center',
      backgroundColor,
      border: `1px solid ${theme.palette.common.white}`,
      borderRadius: 100,
      boxSizing: 'border-box',
      display: 'flex',
      justifyContent: 'center',
      maxHeight: chipHeightPx,
      minHeight: chipHeightPx,
      padding: theme.spacing(0.75),
      width: chipHeightPx,
    },
  })
);

export function WorkflowStateChipIcon({
  backgroundColor,
  workflow,
}: {
  backgroundColor: string;
  workflow: WorkflowStateChipWorkflow;
}): JSX.Element {
  const { classes } = useWorkflowStateIconStyles({ backgroundColor });

  return (
    <div className={classes.iconContainer}>
      <StateChipIcon workflow={workflow} />
    </div>
  );
}

export function StateChipIcon({ workflow }: { workflow: WorkflowStateChipWorkflow }): JSX.Element {
  if (workflow.state === Apollo.WorkflowState.Template) {
    if (!workflow.publishedAt) return <SmallWorkflowDraftingIcon fill={IconColors.White} />;

    return <SmallTemplatePublishedIcon fill={IconColors.White} />;
  }

  switch (workflow.state) {
    case Apollo.WorkflowState.Complete:
      return <SmallDoubleCheckIcon fill={IconColors.White} />;
    case Apollo.WorkflowState.Negotiating:
    case Apollo.WorkflowState.ReadyForSignature:
      return <SmallWorkflowDraftingIcon fill={IconColors.White} />;
    case Apollo.WorkflowState.Signing:
      return <SmallSigningIcon fill={IconColors.White} />;
    case Apollo.WorkflowState.Signed:
      return <SmallWorkflowSingedIcon fill={IconColors.White} />;
  }
}

export function getWorkflowStateChipBackgroundColor(workflow: WorkflowStateChipWorkflow): string {
  if (workflow.state === Apollo.WorkflowState.Template)
    return !workflow.publishedAt
      ? WorkflowStateColors.DraftingYellow
      : WorkflowStateColors.CompletedGreen;

  return getWorkflowStateColor(workflow.state);
}

export function getWorkflowStateChipText(workflow: WorkflowStateChipWorkflow): string {
  if (workflow.state === Apollo.WorkflowState.Template)
    return !workflow.publishedAt ? 'Draft' : 'Published';

  return getWorkflowStateDisplayString(workflow.state);
}
